import React from 'react';
import { useNavigate } from 'react-router-dom';
import Navbar from '../components/Navbar';

const SecurityCompliance = () => {
  const navigate = useNavigate();

  const handleContactClick = () => {
    navigate('/#contact');
    setTimeout(() => {
      const element = document.getElementById('contact');
      if (element) {
        element.scrollIntoView({ behavior: 'smooth' });
      }
    }, 100);
  };

  return (
    <div className="min-h-screen bg-gray-50">
      <Navbar />
      
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-24">
        {/* Hero Section */}
        <div className="text-center mb-16">
          <h1 className="text-5xl font-bold text-gray-900 mb-6">Security & Compliance</h1>
          <p className="text-xl text-gray-600 max-w-3xl mx-auto">
            Protect your digital assets with comprehensive security solutions and ensure compliance with industry standards.
          </p>
        </div>

        {/* Security Services */}
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-12 mb-16">
          <div className="bg-white rounded-lg shadow-lg p-8">
            <h2 className="text-3xl font-bold text-gray-900 mb-6">Security Services</h2>
            <div className="space-y-6">
              <div className="flex items-start space-x-4">
                <div className="flex-shrink-0">
                  <div className="w-8 h-8 bg-red-600 rounded-full flex items-center justify-center">
                    <i className="fas fa-shield-alt text-white text-sm"></i>
                  </div>
                </div>
                <div>
                  <h3 className="text-lg font-semibold text-gray-900 mb-2">Vulnerability Assessment</h3>
                  <p className="text-gray-600">Comprehensive security testing to identify and address vulnerabilities.</p>
                </div>
              </div>
              
              <div className="flex items-start space-x-4">
                <div className="flex-shrink-0">
                  <div className="w-8 h-8 bg-red-600 rounded-full flex items-center justify-center">
                    <i className="fas fa-user-shield text-white text-sm"></i>
                  </div>
                </div>
                <div>
                  <h3 className="text-lg font-semibold text-gray-900 mb-2">Identity & Access Management</h3>
                  <p className="text-gray-600">Secure authentication and authorization systems.</p>
                </div>
              </div>
              
              <div className="flex items-start space-x-4">
                <div className="flex-shrink-0">
                  <div className="w-8 h-8 bg-red-600 rounded-full flex items-center justify-center">
                    <i className="fas fa-lock text-white text-sm"></i>
                  </div>
                </div>
                <div>
                  <h3 className="text-lg font-semibold text-gray-900 mb-2">Data Encryption</h3>
                  <p className="text-gray-600">End-to-end encryption for data at rest and in transit.</p>
                </div>
              </div>

              <div className="flex items-start space-x-4">
                <div className="flex-shrink-0">
                  <div className="w-8 h-8 bg-red-600 rounded-full flex items-center justify-center">
                    <i className="fas fa-eye text-white text-sm"></i>
                  </div>
                </div>
                <div>
                  <h3 className="text-lg font-semibold text-gray-900 mb-2">Security Monitoring</h3>
                  <p className="text-gray-600">24/7 threat detection and incident response capabilities.</p>
                </div>
              </div>
            </div>
          </div>

          <div className="bg-white rounded-lg shadow-lg p-8">
            <h2 className="text-3xl font-bold text-gray-900 mb-6">Compliance Solutions</h2>
            <div className="space-y-6">
              <div className="flex items-start space-x-4">
                <div className="flex-shrink-0">
                  <div className="w-8 h-8 bg-blue-600 rounded-full flex items-center justify-center">
                    <i className="fas fa-certificate text-white text-sm"></i>
                  </div>
                </div>
                <div>
                  <h3 className="text-lg font-semibold text-gray-900 mb-2">GDPR Compliance</h3>
                  <p className="text-gray-600">Ensure compliance with European data protection regulations.</p>
                </div>
              </div>
              
              <div className="flex items-start space-x-4">
                <div className="flex-shrink-0">
                  <div className="w-8 h-8 bg-blue-600 rounded-full flex items-center justify-center">
                    <i className="fas fa-balance-scale text-white text-sm"></i>
                  </div>
                </div>
                <div>
                  <h3 className="text-lg font-semibold text-gray-900 mb-2">SOC 2 Type II</h3>
                  <p className="text-gray-600">Security and availability controls for service organizations.</p>
                </div>
              </div>
              
              <div className="flex items-start space-x-4">
                <div className="flex-shrink-0">
                  <div className="w-8 h-8 bg-blue-600 rounded-full flex items-center justify-center">
                    <i className="fas fa-heartbeat text-white text-sm"></i>
                  </div>
                </div>
                <div>
                  <h3 className="text-lg font-semibold text-gray-900 mb-2">HIPAA Compliance</h3>
                  <p className="text-gray-600">Healthcare data protection and privacy compliance.</p>
                </div>
              </div>

              <div className="flex items-start space-x-4">
                <div className="flex-shrink-0">
                  <div className="w-8 h-8 bg-blue-600 rounded-full flex items-center justify-center">
                    <i className="fas fa-university text-white text-sm"></i>
                  </div>
                </div>
                <div>
                  <h3 className="text-lg font-semibold text-gray-900 mb-2">Financial Compliance</h3>
                  <p className="text-gray-600">PCI DSS, SOX, and other financial industry standards.</p>
                </div>
              </div>
            </div>
          </div>
        </div>

        {/* Security Framework */}
        <div className="bg-white rounded-lg shadow-lg p-8 mb-16">
          <h2 className="text-3xl font-bold text-gray-900 mb-8 text-center">Our Security Framework</h2>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-8">
            <div className="text-center">
              <div className="w-16 h-16 bg-red-100 rounded-full flex items-center justify-center mx-auto mb-4">
                <i className="fas fa-search text-2xl text-red-600"></i>
              </div>
              <h3 className="text-xl font-semibold text-gray-900 mb-3">Identify</h3>
              <p className="text-gray-600">Asset management, business environment, governance, and risk assessment.</p>
            </div>
            <div className="text-center">
              <div className="w-16 h-16 bg-blue-100 rounded-full flex items-center justify-center mx-auto mb-4">
                <i className="fas fa-shield-alt text-2xl text-blue-600"></i>
              </div>
              <h3 className="text-xl font-semibold text-gray-900 mb-3">Protect</h3>
              <p className="text-gray-600">Access controls, awareness training, data security, and protective technology.</p>
            </div>
            <div className="text-center">
              <div className="w-16 h-16 bg-orange-100 rounded-full flex items-center justify-center mx-auto mb-4">
                <i className="fas fa-exclamation-triangle text-2xl text-orange-600"></i>
              </div>
              <h3 className="text-xl font-semibold text-gray-900 mb-3">Detect</h3>
              <p className="text-gray-600">Continuous monitoring, detection processes, and security event analysis.</p>
            </div>
            <div className="text-center">
              <div className="w-16 h-16 bg-purple-100 rounded-full flex items-center justify-center mx-auto mb-4">
                <i className="fas fa-bolt text-2xl text-purple-600"></i>
              </div>
              <h3 className="text-xl font-semibold text-gray-900 mb-3">Respond</h3>
              <p className="text-gray-600">Response planning, communications, analysis, and mitigation strategies.</p>
            </div>
            <div className="text-center">
              <div className="w-16 h-16 bg-green-100 rounded-full flex items-center justify-center mx-auto mb-4">
                <i className="fas fa-redo text-2xl text-green-600"></i>
              </div>
              <h3 className="text-xl font-semibold text-gray-900 mb-3">Recover</h3>
              <p className="text-gray-600">Recovery planning, improvements, and communication protocols.</p>
            </div>
          </div>
        </div>

        {/* Threat Landscape */}
        <div className="grid grid-cols-1 md:grid-cols-2 gap-8 mb-16">
          <div className="bg-gradient-to-br from-red-500 to-red-600 rounded-lg p-8 text-white">
            <h3 className="text-2xl font-bold mb-4">Common Threats We Protect Against</h3>
            <ul className="space-y-3">
              <li className="flex items-center">
                <i className="fas fa-virus mr-3"></i>
                Malware and ransomware attacks
              </li>
              <li className="flex items-center">
                <i className="fas fa-user-secret mr-3"></i>
                Advanced persistent threats (APTs)
              </li>
              <li className="flex items-center">
                <i className="fas fa-fishing mr-3"></i>
                Phishing and social engineering
              </li>
              <li className="flex items-center">
                <i className="fas fa-database mr-3"></i>
                Data breaches and leaks
              </li>
              <li className="flex items-center">
                <i className="fas fa-wifi mr-3"></i>
                Network intrusions and attacks
              </li>
            </ul>
          </div>

          <div className="bg-gradient-to-br from-blue-500 to-blue-600 rounded-lg p-8 text-white">
            <h3 className="text-2xl font-bold mb-4">Security Technologies</h3>
            <ul className="space-y-3">
              <li className="flex items-center">
                <i className="fas fa-fire mr-3"></i>
                Next-generation firewalls
              </li>
              <li className="flex items-center">
                <i className="fas fa-brain mr-3"></i>
                AI-powered threat detection
              </li>
              <li className="flex items-center">
                <i className="fas fa-key mr-3"></i>
                Multi-factor authentication
              </li>
              <li className="flex items-center">
                <i className="fas fa-network-wired mr-3"></i>
                Network segmentation
              </li>
              <li className="flex items-center">
                <i className="fas fa-cloud-upload-alt mr-3"></i>
                Secure cloud configurations
              </li>
            </ul>
          </div>
        </div>

        {/* Compliance Standards */}
        <div className="bg-gray-900 rounded-lg shadow-lg p-8 text-white mb-16">
          <h2 className="text-3xl font-bold mb-8 text-center">Compliance Standards We Support</h2>
          <div className="grid grid-cols-2 md:grid-cols-4 gap-6">
            <div className="text-center p-4 bg-white bg-opacity-10 rounded-lg">
              <h3 className="font-bold text-lg mb-2">GDPR</h3>
              <p className="text-sm opacity-90">General Data Protection Regulation</p>
            </div>
            <div className="text-center p-4 bg-white bg-opacity-10 rounded-lg">
              <h3 className="font-bold text-lg mb-2">SOC 2</h3>
              <p className="text-sm opacity-90">Service Organization Control 2</p>
            </div>
            <div className="text-center p-4 bg-white bg-opacity-10 rounded-lg">
              <h3 className="font-bold text-lg mb-2">HIPAA</h3>
              <p className="text-sm opacity-90">Health Insurance Portability Act</p>
            </div>
            <div className="text-center p-4 bg-white bg-opacity-10 rounded-lg">
              <h3 className="font-bold text-lg mb-2">PCI DSS</h3>
              <p className="text-sm opacity-90">Payment Card Industry Standard</p>
            </div>
            <div className="text-center p-4 bg-white bg-opacity-10 rounded-lg">
              <h3 className="font-bold text-lg mb-2">ISO 27001</h3>
              <p className="text-sm opacity-90">Information Security Management</p>
            </div>
            <div className="text-center p-4 bg-white bg-opacity-10 rounded-lg">
              <h3 className="font-bold text-lg mb-2">NIST</h3>
              <p className="text-sm opacity-90">Cybersecurity Framework</p>
            </div>
            <div className="text-center p-4 bg-white bg-opacity-10 rounded-lg">
              <h3 className="font-bold text-lg mb-2">CCPA</h3>
              <p className="text-sm opacity-90">California Consumer Privacy Act</p>
            </div>
            <div className="text-center p-4 bg-white bg-opacity-10 rounded-lg">
              <h3 className="font-bold text-lg mb-2">SOX</h3>
              <p className="text-sm opacity-90">Sarbanes-Oxley Act</p>
            </div>
          </div>
        </div>

        {/* Security Process */}
        <div className="bg-white rounded-lg shadow-lg p-8 mb-16">
          <h2 className="text-3xl font-bold text-gray-900 mb-8 text-center">Security Implementation Process</h2>
          <div className="grid grid-cols-1 md:grid-cols-5 gap-6">
            <div className="text-center">
              <div className="w-12 h-12 bg-red-600 rounded-full flex items-center justify-center mx-auto mb-4">
                <span className="text-xl font-bold text-white">1</span>
              </div>
              <h3 className="text-lg font-semibold mb-2">Assessment</h3>
              <p className="text-sm text-gray-600">Security audit and risk analysis</p>
            </div>
            <div className="text-center">
              <div className="w-12 h-12 bg-red-600 rounded-full flex items-center justify-center mx-auto mb-4">
                <span className="text-xl font-bold text-white">2</span>
              </div>
              <h3 className="text-lg font-semibold mb-2">Planning</h3>
              <p className="text-sm text-gray-600">Security strategy and roadmap</p>
            </div>
            <div className="text-center">
              <div className="w-12 h-12 bg-red-600 rounded-full flex items-center justify-center mx-auto mb-4">
                <span className="text-xl font-bold text-white">3</span>
              </div>
              <h3 className="text-lg font-semibold mb-2">Implementation</h3>
              <p className="text-sm text-gray-600">Deploy security controls</p>
            </div>
            <div className="text-center">
              <div className="w-12 h-12 bg-red-600 rounded-full flex items-center justify-center mx-auto mb-4">
                <span className="text-xl font-bold text-white">4</span>
              </div>
              <h3 className="text-lg font-semibold mb-2">Testing</h3>
              <p className="text-sm text-gray-600">Penetration testing and validation</p>
            </div>
            <div className="text-center">
              <div className="w-12 h-12 bg-red-600 rounded-full flex items-center justify-center mx-auto mb-4">
                <span className="text-xl font-bold text-white">5</span>
              </div>
              <h3 className="text-lg font-semibold mb-2">Monitoring</h3>
              <p className="text-sm text-gray-600">Continuous security monitoring</p>
            </div>
          </div>
        </div>

        {/* CTA Section */}
        <div className="text-center">
          <h2 className="text-3xl font-bold text-gray-900 mb-4">Secure Your Digital Future</h2>
          <p className="text-xl text-gray-600 mb-8">Protect your business with our comprehensive security and compliance solutions.</p>
          <button 
            onClick={handleContactClick}
            className="bg-red-600 hover:bg-red-700 text-white px-8 py-3 rounded-lg text-lg font-medium transition-colors"
          >
            Secure Your Business
          </button>
        </div>
      </div>
    </div>
  );
};

export default SecurityCompliance;
